import { notifications } from "@mantine/notifications"
import { useMutation, UseMutationOptions } from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useTranslation } from "react-i18next"

import {
  TraceabilityRequests,
  TraceabilityResponses,
} from "@tfg/types/endpoints/traceability"

import { httpClient } from "~/backend/axios.ts"
import { queryClient } from "~/backend/queryClient.ts"

interface CertificateVariables {
  file: File
  typeId: string
}
interface CreateProductionSiteVariables {
  request: TraceabilityRequests.CreateProductionSite
  certificates: CertificateVariables[]
}

export const useCreateProductionSiteMutation = (
  options?: UseMutationOptions<
    TraceabilityResponses.ProductionSite,
    AxiosError,
    CreateProductionSiteVariables
  >
) => {
  const { t } = useTranslation("suppliers")
  return useMutation({
    ...options,
    mutationFn: async (variables) => {
      const res = (
        await httpClient.post<TraceabilityResponses.ProductionSite>(
          `/production-sites`,
          variables.request
        )
      ).data
      for (const certificate of variables.certificates) {
        const formData = new FormData()
        formData.append("file", certificate.file)
        formData.append("productionSiteId", res.id.toString())
        formData.append("typeId", certificate.typeId)
        await httpClient.post(`v1/certificate/upload`, formData)
      }
      return res
    },
    onSuccess: async (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      notifications.show({
        title: t("productionSites.create.success.title"),
        message: t("productionSites.create.success.message"),
        color: "green",
      })
      await queryClient.invalidateQueries(["listProductionSites"])
    },
    onError: async (error, variables, context) => {
      options?.onError?.(error, variables, context)
      notifications.show({
        title: t("productionSites.create.error"),
        message: error.message,
        color: "red",
      })
    },
  })
}
